import {
  collection, doc, getDoc, getDocs, query, where, orderBy, limit,
  addDoc, updateDoc, serverTimestamp, increment
} from 'firebase/firestore';
import { db, COLLECTIONS } from './config';

// Submit a review
export const submitReview = async (reviewData) => {
  try {
    // Check if student already reviewed this teacher
    const existingQuery = query(
      collection(db, COLLECTIONS.REVIEWS),
      where('studentId', '==', reviewData.studentId),
      where('teacherId', '==', reviewData.teacherId)
    );
    const existing = await getDocs(existingQuery);

    if (!existing.empty) {
      // Update existing review
      const existingDoc = existing.docs[0];
      await updateDoc(doc(db, COLLECTIONS.REVIEWS, existingDoc.id), {
        rating: reviewData.rating,
        comment: reviewData.comment,
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new review
      await addDoc(collection(db, COLLECTIONS.REVIEWS), {
        teacherId: reviewData.teacherId,
        studentId: reviewData.studentId,
        studentName: reviewData.studentName,
        studentPhoto: reviewData.studentPhoto || null,
        bookingId: reviewData.bookingId,
        rating: reviewData.rating,
        comment: reviewData.comment,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }

    // Recalculate teacher's average rating
    await recalculateTeacherRating(reviewData.teacherId);
    return true;
  } catch (error) {
    console.error('Error submitting review:', error);
    throw error;
  }
};

// Get reviews for a teacher
export const getTeacherReviews = async (teacherId, maxResults = 20) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.REVIEWS),
      where('teacherId', '==', teacherId)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs
      .map(d => ({
        id: d.id,
        ...d.data(),
        createdAt: d.data().createdAt?.toDate?.() || d.data().createdAt
      }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
      .slice(0, maxResults);
  } catch (error) {
    console.error('Error getting reviews:', error);
    throw error;
  }
};

// Recalculate teacher average rating
const recalculateTeacherRating = async (teacherId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.REVIEWS),
      where('teacherId', '==', teacherId)
    );

    const snapshot = await getDocs(q);
    const reviews = snapshot.docs.map(d => d.data());

    if (reviews.length === 0) return;

    const totalRating = reviews.reduce((sum, r) => sum + r.rating, 0);
    const averageRating = Math.round((totalRating / reviews.length) * 10) / 10;

    await updateDoc(doc(db, COLLECTIONS.USERS, teacherId), {
      averageRating,
      totalReviews: reviews.length
    });
  } catch (error) {
    console.error('Error recalculating rating:', error);
  }
};

// Get rating breakdown for a teacher
export const getRatingBreakdown = async (teacherId) => {
  try {
    const q = query(
      collection(db, COLLECTIONS.REVIEWS),
      where('teacherId', '==', teacherId)
    );

    const snapshot = await getDocs(q);
    const breakdown = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    snapshot.docs.forEach(d => {
      const rating = Math.round(d.data().rating);
      if (rating >= 1 && rating <= 5) breakdown[rating]++;
    });

    return breakdown;
  } catch (error) {
    console.error('Error getting rating breakdown:', error);
    return { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  }
};
